<template>
  <div class="not-prose card bg-base-100 border border-base-300 shadow-sm overflow-hidden">
    <div class="card-body p-6">
      <h3 class="font-semibold text-primary mb-1">Get the next update</h3>
      <p class="text-secondary text-sm mb-4">
        We'll email you when we ship new features and updates.
      </p>

      <!-- Waiting for auth to resolve -->
      <template v-if="!authChecked">
        <p class="text-secondary text-sm">Loading…</p>
      </template>

      <!-- Logged in: show subscribe form -->
      <template v-else-if="supabaseUser">
        <p v-if="isAlreadySubscribed || success" class="text-success text-sm">
          {{ success ? successMessage : "You're subscribed to product updates." }}
        </p>
        <form v-else @submit.prevent="submit" class="flex flex-col sm:flex-row gap-2">
          <button
            type="submit"
            class="btn btn-primary w-fit"
            :disabled="loading"
          >
            <span v-if="loading" class="loading loading-spinner loading-sm" />
            {{ loading ? 'Subscribing…' : 'Subscribe' }}
          </button>
        </form>
      </template>

      <!-- Not logged in: prompt to log in -->
      <template v-else>
        <p class="text-secondary text-sm mb-3">
          Log in to subscribe to product updates.
        </p>
        <NuxtLink
          to="/login?redirect=/updates&subscribe=1"
          class="btn btn-primary btn-sm w-fit"
        >
          Log in to subscribe
        </NuxtLink>
      </template>

      <p v-if="error" class="text-error text-sm mt-2">
        {{ error }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
const { $supabase } = useNuxtApp()
const supabaseUser = useSupabaseUser()
const supabaseUserData = useSupabaseUserData()
const authChecked = useAuthChecked()
const loading = ref(false)
const success = ref(false)
const successMessage = ref('')
const error = ref('')

const isAlreadySubscribed = ref(false)

watch(
  () => supabaseUserData.value?.personal_settings?.productUpdates,
  (val) => { isAlreadySubscribed.value = !!val },
  { immediate: true, deep: true }
)

async function submit() {
  if (!supabaseUser.value) return

  loading.value = true
  error.value = ''

  try {
    const session = await ($supabase as any).auth.getSession()
    const token = session.data.session?.access_token
    if (!token) {
      error.value = 'Session expired. Please log in again.'
      return
    }

    const res = await $fetch<{ success: boolean; message: string }>('/api/updates/subscribe', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    success.value = true
    successMessage.value = res.message
    isAlreadySubscribed.value = true
    if (supabaseUserData.value?.personal_settings) {
      supabaseUserData.value = {
        ...supabaseUserData.value,
        personal_settings: { ...supabaseUserData.value.personal_settings, productUpdates: true },
      }
    }
  } catch (e: unknown) {
    const err = e as { data?: { message?: string }; statusCode?: number }
    error.value = err?.data?.message || 'Something went wrong. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>
